import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import $ from 'jquery';
import { DateFormatter, DateFormatType } from '../../../../utils/dateFormatter';
import { onEvent } from '../../../../utils/onEvent';
import 'datatables.net-bs5';
import 'datatables.net-buttons';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ToastrService } from 'ngx-toastr';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { TenantInterceptor } from '../../../../interceptors/tenant.interceptor';
import { TenantService } from '../../../../services/tenant.service';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.sass'],
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true }],
})
export class DatatableComponent implements AfterViewInit, OnDestroy {
  @Input() settings: DatatableHandlerSettings = DatatableHandler.getDefaultSettings();
  @Input() events: DatatableHandlerEvents = DatatableHandler.getDefaultEvents();
  @Input() dataSourceUrl!: string;

  @Output() onInit: EventEmitter<DatatableHandler> = new EventEmitter();
  @Output() onAction: EventEmitter<DatatableEvent> = new EventEmitter();

  @ViewChild('table') table!: ElementRef;

  private _handler: DatatableHandler | undefined;

  constructor(
    private _router: Router,
    private _renderer: Renderer2,
    private _oidcSecurityService: OidcSecurityService,
    private toastrService: ToastrService,
    private _tenantService: TenantService,
    private _domSanitizer: DomSanitizer
  ) {}

  ngAfterViewInit() {
    this._handler = new DatatableHandler(
      this.table.nativeElement,
      this.settings,
      this.events,
      this.dataSourceUrl,
      this._router,
      this.onAction,
      this._renderer,
      this._oidcSecurityService,
      this.toastrService,
      this._tenantService,
      this._domSanitizer
    );
    this.onInit.emit(this._handler);
  }

  ngOnDestroy() {
    if (this._handler && this._handler.isCreated) {
      this._handler.destroy();
    }
  }
}

export interface DatatableEvent {
  eventType: string;
  data: any;
  actionId: string;
}

export enum DomType {
  Default,
  ButtonsOnBottom,
  TableOnly,
}

export enum IconType {
  Normal = 'normal',
  Danger = 'danger',
  Success = 'success',
}

export enum ErrorHandlingMode {
  Redirect,
  Toastr,
}

export interface DatatableHandlerSettings {
  disableSearch: boolean;
  buttons: DatatableButtonSettings[];
  additionalColumns: DataTables.ColumnDefsSettings[];
  order: (number | string)[][] | (number | string)[];
  lengthMenu: (string | number)[][];
  pageSize: number | undefined;
  searchString: string | undefined;
  domType: DomType;
  responsive: boolean;
  scrollX: boolean;
  actions: (data: any) => DatatableActionButton[];
  serverSide: boolean;
  additionalRequestData: () => object;
  errorHandlingMode: ErrorHandlingMode;
}

export interface DatatableHandlerEvents {
  createdRow: DataTables.FunctionCreateRow | undefined;
  selectedRow: ((selectedRow: HTMLTableRowElement, data: any) => void) | undefined;
  unselectedRow: ((unselectedRow: HTMLTableRowElement, data: any) => void) | undefined;
  initComplete: DataTables.FunctionInitComplete | undefined;
}

export interface DatatableButtonSettings {
  text: string;
  url?: string;
  action?: () => void;
  class: string;
  enabled?: boolean;
}

export class DatatableActionButton {
  constructor(
    public id: string,
    public title: string,
    public icon: string,
    public url?: string,
    public type: IconType = IconType.Normal,
    public isDisabled: boolean = false
  ) {}
}

// @ts-ignore
$.extend($.fn.dataTableExt.oStdClasses, {
  sFilterInput: 'form-control',
  sLengthSelect: 'form-select',
});

export class DatatableHandler {
  private _dataTable: DataTables.Api | undefined;
  private readonly _apiUrl: string;
  private readonly _token: string;
  private _registeredActionEvents: string[] = [];
  public searchString: string | undefined;
  public pageSize: number | undefined;

  constructor(
    private _tableNode: HTMLTableElement,
    private _settings: DatatableHandlerSettings,
    private _events: DatatableHandlerEvents,
    private _dataSourceUrl: string,
    private _router: Router,
    private _eventEmitter: EventEmitter<DatatableEvent>,
    private _renderer: Renderer2,
    private _oidcSecurityService: OidcSecurityService,
    private _toastrService: ToastrService,
    private _tenantService: TenantService,
    private _domSanitizer: DomSanitizer
  ) {
    if (!_tableNode) {
      throw new Error('Table node is not defined');
    }
    if (!_settings) {
      throw new Error('Settings are not defined');
    }
    if (!_events) {
      throw new Error('Events are not defined');
    }
    if (!_dataSourceUrl) {
      throw new Error('Data source url is not defined');
    }
    if (!_router) {
      throw new Error('Router is not defined');
    }
    if (!_eventEmitter) {
      throw new Error('Event emitter is not defined');
    }
    if (!_renderer) {
      throw new Error('Renderer is not defined');
    }

    this._token = _oidcSecurityService.getAccessToken();
    this._apiUrl = environment.apiUrl;
  }

  get isCreated(): boolean {
    return this._dataTable != null;
  }

  get rowCount(): number {
    return this.isCreated ? this._dataTable?.rows?.length! : 0;
  }

  public tableInstance(): DataTables.Api | undefined {
    return this._dataTable;
  }

  public create(): void {
    if (this.isCreated) {
      return;
    }

    this._dataTable = $(this._tableNode).DataTable({
      dom: DatatableHandler.resolveDomType(this._settings.domType),
      language: {
        searchPlaceholder: 'keresés',
        url: '../../assets/datatables-hungarian.json',
      },
      buttons: DatatableHandler.createButtons(this._settings.buttons, this._router),
      responsive: this._settings.responsive,
      scrollX: this._settings.scrollX,
      serverSide: this._settings.serverSide,
      processing: true,
      ajax: {
        url: this._apiUrl + this._dataSourceUrl,
        headers: {
          Authorization: `Bearer ${this._token}`,
          'x-tenant-id': this._tenantService.activeTenantId ?? '',
        },
        type: 'POST',
        cache: false,
        contentType: 'application/json',
        data: data => {
          type ObjectKey = keyof typeof data;
          this.searchString = (data['search' as ObjectKey] as SearchSettings).value;
          this.pageSize = data['length' as ObjectKey];
          data = DatatableHandler.extractAdditionalData(data, this._settings.additionalRequestData());
          return JSON.stringify(data);
        },
        error: (xhr, error, code) => {
          if (this._settings.errorHandlingMode === ErrorHandlingMode.Redirect) {
            this.handleRedirectAjaxError(xhr.status);
          } else if (this._settings.errorHandlingMode === ErrorHandlingMode.Toastr) {
            this.handleToastrAjaxError(xhr.status);
          }
        },
      },
      rowId: data => data['id'],
      columnDefs: [this.getActionColumnDef()].concat(
        this._settings.additionalColumns,
        this.getDefaultColumnDefinitions()
      ),
      orderMulti: false,
      order: this._settings.order,
      lengthMenu: this._settings.lengthMenu,
      pageLength: this._settings.pageSize,
      search: { search: this._settings.searchString },
      createdRow: this._events.createdRow,
      initComplete: this._events.initComplete,
    });
    this.setSelectEvents();
  }

  public destroy(): void {
    if (this._dataTable != null) {
      this._dataTable.destroy();
    }
  }

  public refresh(callback?: () => void, resetPaging: boolean = false): void {
    if (this._dataTable != null) {
      this._dataTable.ajax.reload(callback, resetPaging);
    }
  }

  public getRowById(id: string): HTMLTableRowElement | null {
    if (this._dataTable == null) {
      return null;
    }
    return this._dataTable.row(`#${id}`).node() as HTMLTableRowElement;
  }

  public getButton(selector?: string | number) {
    return this._dataTable?.buttons(selector);
  }

  public static getDefaultSettings(): DatatableHandlerSettings {
    return {
      disableSearch: false,
      additionalColumns: [],
      buttons: [],
      order: [[1, 'asc']],
      lengthMenu: [
        [10, 20, 40, 60, 100],
        [10, 20, 40, 60, 100],
      ],
      pageSize: 20,
      searchString: undefined,
      domType: DomType.Default,
      responsive: true,
      scrollX: true,
      actions: () => [],
      serverSide: true,
      additionalRequestData: () => {
        return {};
      },
      errorHandlingMode: ErrorHandlingMode.Redirect,
    };
  }

  public static getDefaultEvents(): DatatableHandlerEvents {
    return {
      createdRow: undefined,
      selectedRow: undefined,
      unselectedRow: undefined,
      initComplete: undefined,
    };
  }

  private getActionColumnDef(): DataTables.ColumnDefsSettings {
    return {
      targets: 'column-actions',
      searchable: false,
      orderable: false,
      className: 'column-actions',
      data: null,
      render: data => {
        if (this._settings.actions == null) {
          return '';
        }

        const actionDef = this._settings.actions(data);
        const actionButtons: HTMLElement[] = [];

        for (const action of actionDef) {
          const anchor = this._renderer.createElement('a');
          if (action.url && action.url.length > 0) {
            anchor.dataset.url = action.url;
          }
          anchor.title = action.title;
          anchor.classList.add('dt-action-btn');
          anchor.classList.add(`action--${action.id}`);

          if (action.isDisabled) {
            anchor.classList.add('action-disabled');
          }

          const span = this._renderer.createElement('span');
          span.classList.add('material-icons');
          const iconType = DatatableHandler.mapIconType(action.type);
          if (iconType.length > 0) {
            span.classList.add(iconType);
          }
          span.innerHTML = action.icon;

          anchor.appendChild(span);
          actionButtons.push(anchor);

          this.tryRegisterActionEvent(action.id);
        }

        const div = this._renderer.createElement('div');
        div.classList.add('datatable-actions');
        div.append(...actionButtons);

        return div.outerHTML;
      },
    };
  }

  private static mapIconType(iconType: IconType | undefined) {
    if (!iconType) {
      return '';
    }

    switch (iconType) {
      case 'danger':
        return 'text-danger';
      case 'success':
        return 'text-success';
      case 'normal':
      default:
        return '';
    }
  }

  private static resolveDomType(domType: DomType): string {
    switch (domType) {
      case DomType.TableOnly:
        return 't';
      case DomType.Default:
      default:
        return "<'d-flex flex-row align-items-center mt-3'<'col-10 d-flex'Bf><'col-2 d-flex-reverse'l>>rt<'row'<'col-12 item-center'p>>";
      case DomType.ButtonsOnBottom:
        return "<'d-flex flex-row align-items-center mt-3'<'col-6 d-flex'f><'col-6 d-flex-reverse'l>>rt<'row'<'col-4 item-center'B><'col-4 item-center'p>><'col-4 item-center'>";
    }
  }

  private getDefaultColumnDefinitions(): DataTables.ColumnDefsSettings[] {
    return [
      {
        targets: 'column-hide',
        className: 'column-hide',
        visible: false,
      },
      {
        targets: 'datetime',
        className: 'datetime',
        render: data => DateFormatter.format(data, DateFormatType.DateTime),
      },
      {
        targets: 'yesno',
        className: 'yesno',
        render: data => (data ? 'Igen' : 'Nem'),
      },
      {
        targets: 'percentage',
        className: 'percentage',
        render: data => Math.round(data * 10000) / 100 + '%',
      },
      {
        targets: 'date',
        className: 'date',
        render: data => DateFormatter.format(data, DateFormatType.Date),
      },
      {
        targets: 'long-text-30',
        className: 'long-data-30',
        render: data =>
          data && data.length > 30
            ? `<p class="mb-0" title="${data}">${this.renderSafeText(data.substring(0, 26).concat('...'))}</p>`
            : this.renderSafeText(data),
      },
      {
        targets: 'long-text-50',
        className: 'long-data-50',
        render: data =>
          data && data.length > 50
            ? `<p class="mb-0" title="${data}">${this.renderSafeText(data.substring(0, 46).concat('...'))}</p>`
            : this.renderSafeText(data),
      },
      {
        targets: 'long-text-100',
        className: 'long-data-100',
        render: data =>
          data && data.length > 100
            ? `<p class="mb-0" title="${data}">${this.renderSafeText(data.substring(0, 96).concat('...'))}</p>`
            : this.renderSafeText(data),
      },
      {
        targets: 'long-text-150',
        className: 'long-data-150',
        render: data =>
          data && data.length > 150
            ? `<p class="mb-0" title="${data}">${this.renderSafeText(data.substring(0, 146).concat('...'))}</p>`
            : this.renderSafeText(data),
      },
      {
        targets: '_all',
        render: data =>
          data && data.length > 50
            ? `<p class="mb-0" title="${data}">${this.renderSafeText(data.substring(0, 46).concat('...'))}</p>`
            : this.renderSafeText(data),
      },
    ];
  }

  private renderSafeText(data: any): string | null {
    if (data === null) {
      return null;
    }

    return data
      .toString()
      .replace(new RegExp(/</, 'g'), '&lt;')
      .replace(new RegExp(/>/, 'g'), '&gt;')
      .replace(new RegExp(/\'/, 'g'), '&apos;')
      .replace(new RegExp(/\"/, 'g'), '&quot;')
      .replace(new RegExp(/\`/, 'g'), '&#96;');
  }

  private static extractAdditionalData(data: any, additionalData: any): object {
    for (let prop in additionalData) {
      data[prop] = additionalData[prop];
    }
    return data;
  }

  private static createButtons(
    buttonSettings: DatatableButtonSettings[] | undefined,
    router: Router
  ): DataTables.ButtonSettings[] {
    if (!buttonSettings) {
      return [];
    }

    let buttons: DataTables.ButtonSettings[] = [];
    buttonSettings.forEach(x =>
      buttons.push({
        text: x.text,
        className: `btn ${x.class}`,
        action: () => {
          if (x.action != null) {
            x.action();
          }

          if (x.url != null) {
            router.navigate([x.url]);
          }
        },
        enabled: x.enabled ?? true,
      })
    );

    return buttons;
  }

  private setSelectEvents(): void {
    if (this._events.selectedRow || this._events.unselectedRow) {
      this._tableNode.dataset.selectable = 'true';

      onEvent(
        'click',
        `#${this._tableNode.id} tbody tr[id] td:not(.column-actions)`,
        (target: HTMLTableCellElement) => {
          const row = target.parentNode as HTMLTableRowElement;
          const selectedClass = 'selected';
          const isSelected = row.classList.contains(selectedClass);

          const rows = row.parentNode?.querySelectorAll<HTMLTableRowElement>('tr');
          rows?.forEach(x => x.classList.remove(selectedClass));

          if (isSelected) {
            row.classList.remove(selectedClass);
          } else {
            row.classList.add(selectedClass);
          }

          const data = this._dataTable?.row(row).data();

          if (isSelected && this._events.unselectedRow) {
            this._events.unselectedRow(row, data);
          } else if (this._events.selectedRow) {
            this._events.selectedRow(row, data);
          }
        }
      );
    }
  }

  private tryRegisterActionEvent(actionId: string) {
    if (this._registeredActionEvents.indexOf(actionId) > -1) {
      return;
    }
    onEvent('click', `.action--${actionId}:not(.action-disabled)`, (target: HTMLElement) => {
      this._eventEmitter.emit({
        data: this._dataTable?.row(target.closest('tr')).data(),
        eventType: 'click',
        actionId: actionId,
      });

      const url = target.dataset.url;
      if (url) {
        this._router.navigate([target.dataset.url]);
      }
    });
    this._registeredActionEvents.push(actionId);
  }

  private handleRedirectAjaxError(status: number): void {
    switch (status) {
      case 500: {
        this._router.navigate(['error/500']);
        return;
      }
      case 404: {
        this._router.navigate(['error/404']);
        return;
      }
      default: {
        this._router.navigate(['error']);
        return;
      }
    }
  }

  private handleToastrAjaxError(status: number): void {
    switch (status) {
      case 500: {
        this._toastrService.error('Váratlan hiba történt!');
        return;
      }
      case 404: {
        this._toastrService.error('Hiba! Nem létező adat!');
        return;
      }
      default:
        this._toastrService.error('Hiba! Nem létező adat!');
        return;
    }
  }
}

class SearchSettings {
  caseInsensitive?: boolean | undefined;
  regex?: boolean | undefined;
  smart?: boolean | undefined;
  value?: string | undefined;
  searchPlaceholder?: string | undefined;
}
